import React, { useState, useEffect } from 'react';
import {
    TextField, Button, Grid, Box, Typography, Checkbox,
    FormControlLabel, Divider, IconButton, List, ListItem,
    ListItemText, ListItemSecondaryAction, Paper, Chip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { useSearchParams } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { useAccesos } from '../contexts/AccesosContext';
import Swal from 'sweetalert2';

function EncuestaForm() {
    const { tienePermiso } = useAccesos();
    const { user } = useUser();
    const server = process.env.REACT_APP_API_ROUTE;
    const [searchParams] = useSearchParams();
    const detalleId = searchParams.get('detalleId');

    const [detalleInfo, setDetalleInfo] = useState(null);
    const [encuesta, setEncuesta] = useState(null);
    const [formData, setFormData] = useState({
        razon: '',
        antecedentes: '',
        embarazada: false,
        adicional: ''
    });
    const [antecedentesQuirurgicos, setAntecedentesQuirurgicos] = useState([]);
    const [nuevoAntecedente, setNuevoAntecedente] = useState('');
    const [estudiosPrevios, setEstudiosPrevios] = useState([]);
    const [loading, setLoading] = useState(false);

    const fetchDetalleInfo = async () => {
        if (!detalleId || detalleId === 'undefined') return;
        try {
            const response = await fetch(`${server}/MgOrdenesDeTrabajoDetalles/imagenes/detalle-completo/${detalleId}`);
            if (response.ok) {
                const data = await response.json();
                setDetalleInfo(data);
            }
        } catch (error) {
            console.error('Error al cargar detalle:', error);
        }
    };

    const fetchEncuesta = async () => {
        if (!detalleId || detalleId === 'undefined') return;
        try {
            const response = await fetch(`${server}/ImgEncuestas/pordetalle/${detalleId}`);
            if (response.ok) {
                const data = await response.json();
                if (data) {
                    setEncuesta(data);
                    setFormData({
                        razon: data.razon || '',
                        antecedentes: data.antecedentes || '',
                        embarazada: data.embarazada || false,
                        adicional: data.adicional || ''
                    });
                    // Cargar antecedentes quirurgicos
                    const antRes = await fetch(`${server}/ImgEncuestas/${data.idEncuesta}/antecedentes`);
                    const antData = await antRes.json();
                    setAntecedentesQuirurgicos(antData.$values || antData || []);
                    // Cargar estudios previos
                    const estRes = await fetch(`${server}/ImgEncuestas/${data.idEncuesta}/estudios-previos`);
                    const estData = await estRes.json();
                    setEstudiosPrevios(estData.$values || estData || []);
                }
            }
        } catch (error) {
            console.error('Error al cargar encuesta:', error);
        }
    };

    useEffect(() => {
        fetchDetalleInfo();
        fetchEncuesta();
    }, [detalleId]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSave = async () => {
        if (!detalleId) {
            Swal.fire('Error', 'Se requiere un ID de detalle de orden', 'error');
            return;
        }
        setLoading(true);
        try {
            const payload = {
                ...formData,
                idDetalle: parseInt(detalleId),
                ingresadoPor: user.codigoUsuario.padEnd(20),
                modificadoPor: user.codigoUsuario.padEnd(20)
            };
            const method = encuesta ? 'PUT' : 'POST';
            const url = encuesta
                ? `${server}/ImgEncuestas/${encuesta.idEncuesta}`
                : `${server}/ImgEncuestas`;

            const response = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                const data = await response.json();
                setEncuesta(data);
                Swal.fire('Éxito', encuesta ? 'Encuesta actualizada' : 'Encuesta creada', 'success');
            } else {
                const errorData = await response.json();
                Swal.fire('Error', errorData.message || 'Error al guardar', 'error');
            }
        } catch (error) {
            Swal.fire('Error', error.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleAgregarAntecedente = async () => {
        if (!tienePermiso('Agregar Antecedente IMG') || !encuesta || !nuevoAntecedente.trim()) return;
        try {
            const response = await fetch(`${server}/ImgEncuestas/${encuesta.idEncuesta}/antecedentes`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    descripcion: nuevoAntecedente,
                    ingresadoPor: user.codigoUsuario.padEnd(20)
                })
            });
            if (response.ok) {
                setNuevoAntecedente('');
                const antRes = await fetch(`${server}/ImgEncuestas/${encuesta.idEncuesta}/antecedentes`);
                const antData = await antRes.json();
                setAntecedentesQuirurgicos(antData.$values || antData || []);
            }
        } catch (error) {
            Swal.fire('Error', error.message, 'error');
        }
    };

    const handleSubirEstudioPrevio = async (e) => {
        if (!tienePermiso('Agregar Estudio Previo IMG') || !encuesta) return;
        const file = e.target.files[0];
        if (!file) return;
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('usuario', user.codigoUsuario.padEnd(20));

            const response = await fetch(
                `${server}/ImgEncuestas/${encuesta.idEncuesta}/estudios-previos`,
                { method: 'POST', body: formData }
            );
            if (response.ok) {
                const estRes = await fetch(`${server}/ImgEncuestas/${encuesta.idEncuesta}/estudios-previos`);
                const estData = await estRes.json();
                setEstudiosPrevios(estData.$values || estData || []);
                Swal.fire('Éxito', 'Estudio previo adjuntado', 'success');
            }
        } catch (error) {
            Swal.fire('Error', error.message, 'error');
        }
    };

    if (!detalleId) {
        return (
            <div className="right_col" role="main">
            <div className="x_content">
                <div className="x_title"><h2>Encuesta Pre-Estudio</h2></div>
                <Typography>Seleccione un detalle de orden para completar la encuesta.</Typography>
            </div>
            </div>
        );
    }

    return (
        <div className="right_col" role="main">
        <div className="x_content">
            <div className="x_title">
                <h2>Encuesta Pre-Estudio — Detalle #{detalleId}</h2>
            </div>

            {detalleInfo && (
                <Box sx={{ p: 1.5, bgcolor: '#e3f2fd', borderRadius: 1, mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Chip label={`Detalle #${detalleInfo.idDetalle}`} size="small" color="primary" />
                    <Typography variant="body2"><strong>Paciente:</strong> {detalleInfo.nombrePaciente}</Typography>
                    <Typography variant="body2"><strong>Examen:</strong> {detalleInfo.nombreProducto}</Typography>
                    <Chip label={detalleInfo.estadoFlujo || 'pendiente'} size="small" variant="outlined" />
                </Box>
            )}

            <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1, mb: 2 }}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Razón / Motivo del estudio"
                            name="razon"
                            value={formData.razon}
                            onChange={handleChange}
                            multiline
                            rows={2}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Antecedentes clínicos"
                            name="antecedentes"
                            value={formData.antecedentes}
                            onChange={handleChange}
                            multiline
                            rows={3}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    name="embarazada"
                                    checked={formData.embarazada}
                                    onChange={handleChange}
                                />
                            }
                            label="¿Embarazada?"
                        />
                    </Grid>
                    <Grid item xs={12} md={9}>
                        <TextField
                            fullWidth
                            label="Notas adicionales"
                            name="adicional"
                            value={formData.adicional}
                            onChange={handleChange}
                            multiline
                            rows={2}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Button
                            variant="contained"
                            onClick={handleSave}
                            disabled={loading || !tienePermiso(encuesta ? 'Editar Encuesta IMG' : 'Crear Encuesta IMG')}
                        >
                            {loading ? 'Guardando...' : 'Guardar Encuesta'}
                        </Button>
                    </Grid>
                </Grid>
            </Box>

            {encuesta && (
                <>
                    <Divider sx={{ my: 2 }} />

                    {/* Antecedentes Quirúrgicos */}
                    <Typography variant="h6">Antecedentes Quirúrgicos</Typography>
                    <Grid container spacing={1} sx={{ mb: 2 }}>
                        <Grid item xs={12} md={8}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Nueva cirugía / antecedente"
                                value={nuevoAntecedente}
                                onChange={(e) => setNuevoAntecedente(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleAgregarAntecedente()}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Button
                                variant="outlined"
                                onClick={handleAgregarAntecedente}
                                disabled={!tienePermiso('Agregar Antecedente IMG')}
                            >
                                Agregar
                            </Button>
                        </Grid>
                    </Grid>
                    <List dense>
                        {antecedentesQuirurgicos.map((ant, idx) => (
                            <ListItem key={ant.id || idx}>
                                <ListItemText primary={ant.descripcion} />
                            </ListItem>
                        ))}
                        {antecedentesQuirurgicos.length === 0 && (
                            <ListItem><ListItemText primary="Sin antecedentes registrados" /></ListItem>
                        )}
                    </List>

                    <Divider sx={{ my: 2 }} />

                    {/* Estudios Previos */}
                    <Typography variant="h6">Estudios Previos</Typography>
                    <Box sx={{ mb: 1 }}>
                        <Button
                            variant="outlined"
                            component="label"
                            startIcon={<AttachFileIcon />}
                            disabled={!tienePermiso('Agregar Estudio Previo IMG')}
                        >
                            Adjuntar Archivo
                            <input type="file" hidden onChange={handleSubirEstudioPrevio} />
                        </Button>
                    </Box>
                    <List dense>
                        {estudiosPrevios.map((est, idx) => (
                            <ListItem key={est.id || idx}>
                                <ListItemText
                                    primary={est.nombreArchivo || `Archivo ${idx + 1}`}
                                    secondary={est.fechaSubida ? new Date(est.fechaSubida).toLocaleString() : ''}
                                />
                            </ListItem>
                        ))}
                        {estudiosPrevios.length === 0 && (
                            <ListItem><ListItemText primary="Sin estudios previos adjuntos" /></ListItem>
                        )}
                    </List>
                </>
            )}
        </div>
        </div>
    );
}

export default EncuestaForm;
